import { getDocs, collection, doc, getDoc, addDoc, updateDoc, deleteDoc, query, where, orderBy, limit } from "firebase/firestore";
import { db } from '../../../../firebase/config';
import {
    designationIsIncharge,
    designationIsMisIncharge,
    normalizeChangeOfVendorStatus,
} from '../../../../utils/designationRole';
import { fetchCompanyBankId } from '../../../companies/service/companiesService';
import { fetchCompaniesData } from '../../../companies/service/companiesService';
import type { Company } from '../../../companies/service/companiesService';

export type Branch = {
    id: string;
    bankId: string;
    branchCode?: string;
    address?: string;
    psoId?: string;
    psoName?: string;
    psoEmail?: string;
    psoContact?: string;
    psoDesignation?: string;
    [key: string]: any;
};

export type ChangeOfVendor = {
    id: string;
    requestId?: string;
    bankId: string;
    branchId: string;
    companiesId: string;
    psoId?: string;
    createdBy?: string;
    createdAt?: any;
    updatedAt?: any;
    status?: 'Pending' | 'Approved' | 'Rejected' | 'Cancelled' | 'Forwarded to Head';
    requestDate?: any;
    deploymentDate?: any;
    approvalDate?: any;
    rejectDate?: any;
    branch?: Branch | null;
    company?: Company | null;
    reason?: string;
    requestorName?: string;
    requestorDesignation?: string;
    complaintStartDate?: string;
    followUpDates?: string[];
    forwardedAt?: any;
    forwardedBy?: string;
    forwardedByName?: string;
    rejectReason?: string;
    rejectedBy?: string;
    rejectedByName?: string;
    rejectionStage?: 'Regional' | 'Head';
    approvedBy?: string;
    approvedByName?: string;
    resubmitted?: boolean;
    resubmissionReason?: string;
    resubmittedAt?: any;
};

/**
 * Fetch bankId for a user (PSO or Bank)
 */
export async function fetchChangeOfVendorBankId(uid: string, isBankUser: boolean = false): Promise<string | null> {
    return await fetchCompanyBankId(uid, isBankUser);
}

/**
 * Fetch branches for a bank, optionally filtered by PSO ID
 */
export async function fetchBranches(bankId: string, psoId?: string): Promise<Branch[]> {
    try {
        const branchSnap = await getDocs(collection(db, "banks", bankId, "branch"));
        let branches = branchSnap.docs.map(doc => ({
            id: doc.id,
            bankId,
            ...doc.data()
        })) as Branch[];

        // Fetch locations to get region names
        const locSnap = await getDocs(collection(db, "locations"));
        const locMap = new Map(locSnap.docs.map(d => [d.id, d.data().name]));

        // Fetch PSOs to enrich branch data with PSO information
        const psoSnap = await getDocs(collection(db, "banks", bankId, "pos"));
        const psoList = psoSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as { id: string; name?: string; email?: string; contact?: string; designation?: string }));

        // Enrich branches with PSO information and locationName
        branches = branches.map(branch => {
            const pso = branch.psoId ? psoList.find(p => p.id === branch.psoId) : null;
            const locationName = branch.locationId ? locMap.get(branch.locationId) : null;

            return {
                ...branch,
                locationName: locationName || branch.locationName || "N/A",
                psoName: pso?.name || branch.psoName,
                psoEmail: pso?.email || branch.psoEmail,
                psoContact: pso?.contact || branch.psoContact,
                psoDesignation: pso?.designation || branch.psoDesignation
            };
        });

        // Filter by PSO ID if provided
        if (psoId) {
            branches = branches.filter(branch => branch.psoId === psoId);
        }

        return branches;
    } catch (error) {
        console.error('Error fetching branches:', error);
        throw error;
    }
}

/**
 * Fetch change of vendor requests for a bank, optionally filtered by PSO ID, incharge designation, and company type
 */
export async function fetchChangeOfVendorsData(
    uid: string,
    isBankUser: boolean = false,
    isRegionalHead: boolean = false,
    psoDesignation?: string,
    isHead: boolean = false
): Promise<ChangeOfVendor[]> {
    // Find bankId
    const bankId = await fetchChangeOfVendorBankId(uid, isBankUser);
    if (!bankId) {
        console.log('No bank found for user:', uid);
        return [];
    }

    try {
        // Fetch change of vendor requests from banks/{bankId}/changeOfVendor
        const changeOfVendorsSnap = await getDocs(collection(db, "banks", bankId, "changeOfVendor"));
        let changeOfVendors = changeOfVendorsSnap.docs.map(doc => ({
            id: doc.id,
            bankId,
            ...doc.data()
        })) as ChangeOfVendor[];

        console.log(`Found ${changeOfVendors.length} change of vendor requests in collection`);

        // Check if user is an incharge (handles "In Charge", "In-Charge", "Incharge", etc.)
        const isIncharge = designationIsIncharge(psoDesignation);
        const inchargeType = psoDesignation?.replace(/_/g, ' ').toLowerCase() || '';
        const isInchargeCCTV = isIncharge && inchargeType.includes('cctv');
        const isInchargeBAMS = isIncharge && inchargeType.includes('bams');
        const isInchargeGuarding = isIncharge && inchargeType.includes('guarding');
        const isInchargeMIS = designationIsMisIncharge(psoDesignation);

        // Filter by PSO ID if not bank user, not regional head, not head, and not incharge
        // Incharge users see approved requests based on their type
        if (!isBankUser && !isRegionalHead && !isHead && !isIncharge) {
            const beforeFilter = changeOfVendors.length;
            changeOfVendors = changeOfVendors.filter(changeOfVendor => changeOfVendor.psoId === uid);
            console.log(`Filtered from ${beforeFilter} to ${changeOfVendors.length} requests for PSO ${uid}`);
        }

        // Fetch branches and companies to enrich change of vendor data
        const branches = await fetchBranches(bankId);
        const companies = await fetchCompaniesData(uid, isBankUser);

        // Enrich change of vendors with branch and company data
        let enrichedChangeOfVendors = changeOfVendors.map(changeOfVendor => {
            const branch = branches.find(b => b.id === changeOfVendor.branchId);
            const company = companies.find(c => c.id === changeOfVendor.companiesId);
            return {
                ...changeOfVendor,
                branch: branch || null,
                company: company || null,
                status: changeOfVendor.status || 'Pending'
            };
        });

        // Filter for incharge users: only show approved requests matching their company type
        if (isIncharge) {
            const beforeInchargeFilter = enrichedChangeOfVendors.length;

            // Only show approved requests to incharge users
            enrichedChangeOfVendors = enrichedChangeOfVendors.filter(
                (changeOfVendor) =>
                    normalizeChangeOfVendorStatus(changeOfVendor.status) === 'approved'
            );

            // Filter by company type based on incharge designation
            if (isInchargeCCTV) {
                enrichedChangeOfVendors = enrichedChangeOfVendors.filter(changeOfVendor =>
                    changeOfVendor.company?.companyType === 'CCTV'
                );
                console.log(`Filtered for Incharge CCTV: showing only approved CCTV requests`);
            } else if (isInchargeBAMS) {
                enrichedChangeOfVendors = enrichedChangeOfVendors.filter(changeOfVendor =>
                    changeOfVendor.company?.companyType === 'BAMS'
                );
                console.log(`Filtered for Incharge BAMS: showing only approved BAMS requests`);
            } else if (isInchargeGuarding) {
                enrichedChangeOfVendors = enrichedChangeOfVendors.filter(changeOfVendor =>
                    changeOfVendor.company?.companyType === 'Guard Company'
                );
                console.log(`Filtered for Incharge Guarding: showing only approved Guard Company requests`);
            } else if (isInchargeMIS) {
                // Incharge MIS sees all approved requests
                console.log(`Filtered for Incharge MIS: showing all approved requests`);
            }

            console.log(`Filtered from ${beforeInchargeFilter} to ${enrichedChangeOfVendors.length} approved requests for incharge ${psoDesignation}`);
        }

        // Sort by createdAt descending (latest on top)
        enrichedChangeOfVendors.sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt ? new Date(a.createdAt) : new Date(0);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : b.createdAt ? new Date(b.createdAt) : new Date(0);
            return dateB.getTime() - dateA.getTime();
        });

        console.log(`Returning ${enrichedChangeOfVendors.length} enriched change of vendor requests`);
        return enrichedChangeOfVendors;
    } catch (error) {
        console.error('Error fetching change of vendor requests:', error);
        // If collection doesn't exist, return empty array (it's a new collection)
        return [];
    }
}

/**
 * Generate a unique request ID in format RQ-000-xxxxxxx
 */
export async function generateRequestId(bankId: string): Promise<string> {
    try {
        // Get all change of vendor requests to find the highest number
        const requestsSnap = await getDocs(collection(db, "banks", bankId, "changeOfVendor"));

        let maxNumber = 0;
        requestsSnap.docs.forEach(doc => {
            const data = doc.data();
            if (data.requestId && data.requestId.startsWith('RQ-')) {
                const parts = data.requestId.split('-');
                if (parts.length >= 3) {
                    const number = parseInt(parts[1]);
                    if (!isNaN(number) && number > maxNumber) {
                        maxNumber = number;
                    }
                }
            }
        });

        const nextNumber = maxNumber + 1;
        const timestamp = Date.now().toString().slice(-7); // Last 7 digits of timestamp
        return `RQ-${nextNumber.toString().padStart(3, '0')}-${timestamp}`;
    } catch (error) {
        console.error('Error generating request ID:', error);
        // Fallback to timestamp-based ID
        const timestamp = Date.now().toString().slice(-7);
        return `RQ-001-${timestamp}`;
    }
}

/**
 * Check if a request already exists for a branch and company type
 */
export async function checkExistingRequest(
    bankId: string,
    branchId: string,
    companyType: string
): Promise<ChangeOfVendor | null> {
    try {
        // Fetch all change of vendor requests for this branch
        const requestsSnap = await getDocs(collection(db, "banks", bankId, "changeOfVendor"));
        const requests = requestsSnap.docs.map(doc => ({
            id: doc.id,
            bankId,
            ...doc.data()
        })) as ChangeOfVendor[];

        // Fetch companies to check company type - need to get bankId first
        const companiesSnap = await getDocs(collection(db, "banks", bankId, "companies"));
        const companiesList = companiesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));

        // Check if there's a pending or approved request for this branch with the same company type
        // Only block if status is 'Pending' or 'Approved' or 'Forwarded to Head'
        // Allow if status is 'Rejected', 'Cancelled', or 'Completed'
        for (const request of requests) {
            if (request.branchId === branchId && (request.status === 'Pending' || request.status === 'Approved' || request.status === 'Forwarded to Head')) {
                const company = companiesList.find(c => c.id === request.companiesId);
                if (company) {
                    // Check company type - might be in companyType field or need to fetch from global
                    let requestCompanyType = company.companyType;
                    if (!requestCompanyType && company.companyId) {
                        try {
                            const globalCompanyDoc = await getDoc(doc(db, "companies", company.companyId));
                            if (globalCompanyDoc.exists()) {
                                const globalData = globalCompanyDoc.data();
                                requestCompanyType = globalData.companyType;
                            }
                        } catch (err) {
                            console.error('Error fetching global company:', err);
                        }
                    }
                    if (requestCompanyType === companyType) {
                        return request;
                    }
                }
            }
        }

        return null;
    } catch (error) {
        console.error('Error checking existing request:', error);
        return null;
    }
}

/**
 * Create a new change of vendor request
 */
export async function createChangeOfVendor(
    bankId: string,
    branchId: string,
    companiesId: string,
    psoId: string,
    requestDate?: Date,
    deploymentDate?: Date,
    reason?: string,
    requestorName?: string,
    requestorDesignation?: string,
    complaintStartDate?: string,
    followUpDates?: string[]
): Promise<string> {
    try {
        console.log('createChangeOfVendor called with:', { bankId, branchId, companiesId, psoId, requestDate, deploymentDate });

        // Generate request ID
        const requestId = await generateRequestId(bankId);
        console.log('Generated request ID:', requestId);

        const changeOfVendorData = {
            requestId,
            branchId,
            companiesId,
            psoId,
            createdBy: psoId,
            status: 'Pending',
            requestDate: requestDate || new Date(),
            deploymentDate: deploymentDate || null,
            reason: reason || '',
            requestorName: requestorName || '',
            requestorDesignation: requestorDesignation || '',
            complaintStartDate: complaintStartDate || '',
            followUpDates: followUpDates || [],
            createdAt: new Date(),
            updatedAt: new Date()
        };

        console.log('Creating document in collection: banks/', bankId, '/changeOfVendor');
        console.log('Document data:', changeOfVendorData);

        const changeOfVendorRef = await addDoc(collection(db, "banks", bankId, "changeOfVendor"), changeOfVendorData);

        console.log('Document created successfully with ID:', changeOfVendorRef.id);
        return changeOfVendorRef.id;
    } catch (error: any) {
        console.error('Error creating change of vendor:', error);
        console.error('Error details:', {
            message: error.message,
            code: error.code,
            stack: error.stack
        });
        throw error;
    }
}

/**
 * Update change of vendor status with multi-stage support
 */
export async function updateChangeOfVendorStatus(
    bankId: string,
    changeOfVendorId: string,
    status: 'Pending' | 'Approved' | 'Rejected' | 'Cancelled' | 'Forwarded to Head',
    extra?: {
        rejectReason?: string;
        rejectedBy?: string;
        rejectedByName?: string;
        rejectionStage?: 'Regional' | 'Head';
        forwardedBy?: string;
        forwardedByName?: string;
        approvedBy?: string;
        approvedByName?: string;
    }
): Promise<void> {
    try {
        const changeOfVendorRef = doc(db, "banks", bankId, "changeOfVendor", changeOfVendorId);
        const updateData: any = {
            status,
            updatedAt: new Date()
        };

        // Add stage-specific data based on status
        if (status === 'Approved') {
            updateData.approvalDate = new Date();
            updateData.rejectDate = null;
            if (extra?.approvedBy) updateData.approvedBy = extra.approvedBy;
            if (extra?.approvedByName) updateData.approvedByName = extra.approvedByName;
        } else if (status === 'Rejected') {
            updateData.rejectDate = new Date();
            updateData.approvalDate = null;
            if (extra?.rejectReason !== undefined) updateData.rejectReason = extra.rejectReason;
            if (extra?.rejectedBy) updateData.rejectedBy = extra.rejectedBy;
            if (extra?.rejectedByName) updateData.rejectedByName = extra.rejectedByName;
            if (extra?.rejectionStage) updateData.rejectionStage = extra.rejectionStage;
        } else if (status === 'Forwarded to Head') {
            updateData.forwardedAt = new Date();
            if (extra?.forwardedBy) updateData.forwardedBy = extra.forwardedBy;
            if (extra?.forwardedByName) updateData.forwardedByName = extra.forwardedByName;
        }

        await updateDoc(changeOfVendorRef, updateData);
    } catch (error) {
        console.error('Error updating change of vendor status:', error);
        throw error;
    }
}

/**
 * Resubmit a change of vendor request
 */
export async function resubmitChangeOfVendor(
    bankId: string,
    changeOfVendorId: string,
    reason: string,
    uid: string,
    extraFields?: {
        reason?: string;
        requestorName?: string;
        requestorDesignation?: string;
        complaintStartDate?: string;
        followUpDates?: string[];
    }
): Promise<void> {
    try {
        const changeOfVendorRef = doc(db, "banks", bankId, "changeOfVendor", changeOfVendorId);
        const updateData: any = {
            status: 'Pending',
            resubmitted: true,
            resubmissionReason: reason,
            resubmittedAt: new Date(),
            updatedAt: new Date(),
            updatedBy: uid,
            // Clear any previous rejection details
            rejectDate: null,
            rejectReason: null,
            rejectedBy: null,
            rejectedByName: null,
            rejectionStage: null,
        };

        if (extraFields) {
            if (extraFields.reason !== undefined) updateData.reason = extraFields.reason;
            if (extraFields.requestorName !== undefined) updateData.requestorName = extraFields.requestorName;
            if (extraFields.requestorDesignation !== undefined) updateData.requestorDesignation = extraFields.requestorDesignation;
            if (extraFields.complaintStartDate !== undefined) updateData.complaintStartDate = extraFields.complaintStartDate;
            if (extraFields.followUpDates !== undefined) updateData.followUpDates = extraFields.followUpDates;
        }

        await updateDoc(changeOfVendorRef, updateData);
    } catch (error) {
        console.error('Error resubmitting change of vendor request:', error);
        throw error;
    }
}

/**
 * Update deployment date for a change of vendor request
 */
export async function updateDeploymentDate(
    bankId: string,
    changeOfVendorId: string,
    deploymentDate: Date
): Promise<void> {
    try {
        const changeOfVendorRef = doc(db, "banks", bankId, "changeOfVendor", changeOfVendorId);
        await updateDoc(changeOfVendorRef, {
            deploymentDate,
            updatedAt: new Date()
        });
    } catch (error) {
        console.error('Error updating deployment date:', error);
        throw error;
    }
}

/**
 * Delete a change of vendor request
 */
export async function deleteChangeOfVendor(bankId: string, changeOfVendorId: string): Promise<void> {
    try {
        const changeOfVendorRef = doc(db, "banks", bankId, "changeOfVendor", changeOfVendorId);
        await deleteDoc(changeOfVendorRef);
    } catch (error) {
        console.error('Error deleting change of vendor:', error);
        throw error;
    }
}

/**
 * Check if a change of vendor conflict exists (same company type already deployed to branch)
 */
export async function checkChangeOfVendorConflict(
    bankId: string,
    branchId: string,
    companiesId: string
): Promise<{ hasConflict: boolean; existingChangeOfVendor?: ChangeOfVendor; existingCompany?: Company }> {
    try {
        // Fetch all deployments for this branch to check conflicts
        const deploymentsSnap = await getDocs(collection(db, "banks", bankId, "companyDeployment"));
        const deployments = deploymentsSnap.docs
            .map(doc => ({ id: doc.id, bankId, ...doc.data() } as any))
            .filter(d => d.branchId === branchId);

        // Fetch the company being requested
        const companiesSnap = await getDocs(collection(db, "banks", bankId, "companies"));
        const companiesList = companiesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));

        const targetCompany = companiesList.find(c => c.id === companiesId);
        if (!targetCompany) {
            return { hasConflict: false };
        }

        // Check if target company has companyId to fetch from global collection
        let targetCompanyType = targetCompany.companyType;
        if (targetCompany.companyId) {
            try {
                const globalCompanyDoc = await getDoc(doc(db, "companies", targetCompany.companyId));
                if (globalCompanyDoc.exists()) {
                    const globalData = globalCompanyDoc.data();
                    targetCompanyType = targetCompany.companyType || globalData.companyType;
                }
            } catch (err) {
                console.error('Error fetching global company:', err);
            }
        }

        // Check if any existing deployment has a company of the same type
        for (const deployment of deployments) {
            const existingCompany = companiesList.find(c => c.id === deployment.companiesId);
            if (!existingCompany) continue;

            let existingCompanyType = existingCompany.companyType;
            if (existingCompany.companyId) {
                try {
                    const globalCompanyDoc = await getDoc(doc(db, "companies", existingCompany.companyId));
                    if (globalCompanyDoc.exists()) {
                        const globalData = globalCompanyDoc.data();
                        existingCompanyType = existingCompany.companyType || globalData.companyType;
                    }
                } catch (err) {
                    console.error('Error fetching global company:', err);
                }
            }

            if (existingCompanyType === targetCompanyType) {
                return {
                    hasConflict: true,
                    existingCompany: existingCompany as Company
                };
            }
        }

        return { hasConflict: false };
    } catch (error) {
        console.error('Error checking change of vendor conflict:', error);
        throw error;
    }
}

/**
 * Fetch all current deployments for a bank
 */
export async function fetchAllDeployments(bankId: string): Promise<any[]> {
    try {
        const deploymentsSnap = await getDocs(collection(db, "banks", bankId, "companyDeployment"));
        return deploymentsSnap.docs.map(doc => ({ id: doc.id, bankId, ...doc.data() }));
    } catch (error) {
        console.error('Error fetching all deployments:', error);
        return [];
    }
}

